import type { KycFileCertificationChoiceDto } from '@dealstack/shared/lib/generated';
import { uploadFileV2 } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	addProofOfPIV,
	deleteProofOfPIV,
	getProofsOfPIV,
	resetInheritedKycItems,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { KycResetScope } from '@dealstack/shared/lib/common/business/kyc';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useFileUploadErrorHandler } from '@dealstack/frontend/investor/hooks/useFileUploadErrorHandler';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';
import { useKycFileConfirmation } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/useKycFileConfirmation';
import {
	hasUnconfirmedKycFiles,
	isKycRejected,
} from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
	noSave,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';

export enum PIV_PROOFS_FORM_FIELD {
	SHARE_REGISTER = 'SHARE_REGISTER',
	CERTIFICATE_OF_REGISTRATION = 'CERTIFICATE_OF_REGISTRATION',
	ARTICLES_OF_ASSOCIATION = 'ARTICLES_OF_ASSOCIATION',
	SIGNATORY_POWER = 'SIGNATORY_POWER',
}

export interface PivProofValues {
	[PIV_PROOFS_FORM_FIELD.SHARE_REGISTER]: boolean;
	[PIV_PROOFS_FORM_FIELD.CERTIFICATE_OF_REGISTRATION]: boolean;
	[PIV_PROOFS_FORM_FIELD.ARTICLES_OF_ASSOCIATION]: boolean;
	[PIV_PROOFS_FORM_FIELD.SIGNATORY_POWER]: boolean;
}

export const useJoinAndAllocationPIVProofController = () => {
	const { setIsContinueDisabled, investmentDetails, kycSetting } = useContext(
		investmentJoinAndAllocationContext
	);
	const tInvestor = useTranslationInvestor();
	const handleUploadError = useFileUploadErrorHandler();
	const kycWorkflowId = investmentDetails?.activeKycWorkflowId ?? '';

	const pivProofsQuery = useQuery({
		queryKey: ['kyc', 'pivProofs', kycWorkflowId],
		queryFn: () => getProofsOfPIV(kycWorkflowId),
		enabled: !!kycWorkflowId,
	});
	const uploadFileMutation = useMutation({
		mutationFn: uploadFileV2,
		onError: handleUploadError,
	});
	const addProofMutation = useMutation({
		mutationFn: (variables: Parameters<typeof addProofOfPIV>[0]) => addProofOfPIV(variables),
	});
	const deleteProofMutation = useMutation({
		mutationFn: (variables: Parameters<typeof deleteProofOfPIV>[0]) =>
			deleteProofOfPIV(variables),
	});
	const { createConfirmHandler } = useKycFileConfirmation();

	const onFileDrop = async (file: File, certification?: KycFileCertificationChoiceDto) => {
		try {
			const { payload } = await uploadFileMutation.mutateAsync(file);
			if (!payload) {
				return;
			}
			await addProofMutation.mutateAsync({
				fileId: payload.id,
				kycWorkflowId,
				certification,
			});
			showToastV2({ title: tInvestor('investment.toast.proof_uploaded') });
			void pivProofsQuery.refetch();
		} catch {
			showToastV2({
				title: tInvestor('investment.toast.proof_upload_failed'),
				state: 'error',
			});
		}
	};

	const proofFilesDetails = pivProofsQuery.data?.payload;

	const onConfirm = (proofId: string) =>
		createConfirmHandler(proofId, () => void pivProofsQuery.refetch());

	const onResetAll = async () => {
		await resetInheritedKycItems(kycWorkflowId, KycResetScope.VEHICLE);
		void pivProofsQuery.refetch();
	};

	const onDelete = (proofId: string) => () => {
		const proofToDelete = proofFilesDetails?.find((proof) => proof.id === proofId);
		if (!proofToDelete) {
			return;
		}

		deleteProofMutation.mutate(
			{ fileId: proofToDelete.id, kycWorkflowId },
			{
				onSuccess: () => {
					showToastV2({ title: tInvestor('investment.toast.proof_removed') });
					void pivProofsQuery.refetch();
				},
			}
		);
	};

	const pivProofsForm = useForm<PivProofValues>();

	useEffect(() => {
		const isAnyRejected = !!proofFilesDetails?.some((proof) => isKycRejected(proof.status));
		const hasUnconfirmed = hasUnconfirmedKycFiles(proofFilesDetails ?? []);

		setIsContinueDisabled(!proofFilesDetails?.length || isAnyRejected || hasUnconfirmed);
	}, [proofFilesDetails, pivProofsForm.watch()]);

	useWizardStep(noSave);

	return {
		refetchProofs: () => void pivProofsQuery.refetch(),
		uploadFileRequest: uploadFileMutation,
		proofFilesDetails,
		onDelete,
		onConfirm,
		onResetAll,
		pivProofsForm,
		onFileDrop,
		investmentDetails,
		kycSetting,
	};
};
